import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import Button from "components/button";
import Card from "components/card";
import { getAnnouncements } from "services/announcements";
import { getEvents, rsvpEvent } from "services/events";
import { getMyBusiness } from "services/businessService";
import { useToast } from "components/Toast";
import { useAuthContext } from "hooks/AuthContext";

interface Announcement {
  id: number;
  title: string;
  content: string;
  created_at?: string;
}

interface EventItem {
  id: number;
  name: string;
  event_date?: string;
  rsvpStatus?: string;
}

const UserDefault: React.FC = () => {
  const { user } = useAuthContext();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasBusiness, setHasBusiness] = useState(false);
  const pollRef = useRef<number | null>(null);
  const toast = useToast();

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!mounted) return;
      setLoading(true);
      try {
        const annRes = await getAnnouncements();
        const evRes = await getEvents();
        const annData = annRes.data?.data ?? annRes.data ?? [];
        const evData = evRes.data?.data ?? [];
        
        let userHasBusiness = false;
        try {
          const businessRes = await getMyBusiness();
          userHasBusiness = !!businessRes;
        } catch (err) {
          // User doesn't have a business or error fetching it
          userHasBusiness = false;
        }
        
        if (mounted) {
          setAnnouncements(Array.isArray(annData) ? annData.slice(0, 3) : []);
          setEvents(Array.isArray(evData) ? evData.slice(0, 3) : []);
          setHasBusiness(userHasBusiness);
        }
      } catch (err) {
        console.error(err);
        if (mounted) {
          setAnnouncements([]);
          setEvents([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    // initial load
    load();

    // Poll every 30s for live preview updates
    pollRef.current = window.setInterval(() => {
      load();
    }, 30000);

    return () => {
      mounted = false;
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [user]);

  const handleRsvp = async (id: number) => {
    // Optimistically update UI
    setEvents((prev) =>
      prev.map((ev) => (ev.id === id ? { ...ev, rsvpStatus: "going" } : ev))
    );
    try {
      await rsvpEvent(id);
      toast.showSuccess("RSVP successful");
    } catch (err) {
      console.error(err);
      // revert optimistic update on failure
      setEvents((prev) =>
        prev.map((ev) => (ev.id === id ? { ...ev, rsvpStatus: undefined } : ev))
      );
      toast.showError("Failed to RSVP");
    }
  };

  return (
    <div className="p-4">
      <div className="mb-4">
        <h2 className="text-2xl font-semibold">
          {user ? `Welcome, ${user.name || user.email}` : "Your Panel"}
        </h2>
        <p className="text-sm text-gray-600">Quick links and recent activity</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card extra="p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Announcements</h3>
              <p className="text-sm text-gray-500">Latest announcements</p>
            </div>
            <div>
              <Link to="/user/announcements">
                <Button size="sm" variant="outline">
                  All
                </Button>
              </Link>
            </div>
          </div>

          <div className="mt-3 space-y-3" aria-live="polite">
            {loading ? (
              <div>Loading...</div>
            ) : announcements.length === 0 ? (
              <div className="text-sm text-gray-500">No announcements</div>
            ) : (
              announcements.map((a) => (
                <article
                  key={a.id}
                  className="rounded-lg bg-white p-3 shadow-sm dark:bg-navy-700 dark:text-gray-200"
                  role="article"
                  aria-labelledby={`ann-${a.id}-title`}
                  tabIndex={0}
                >
                  <h4
                    id={`ann-${a.id}-title`}
                    className="text-sm font-semibold"
                  >
                    {a.title}
                  </h4>
                  <p className="line-clamp-2 text-xs text-gray-500 dark:text-gray-300">
                    {a.content}
                  </p>
                </article>
              ))
            )}
          </div>
        </Card>

        <Card extra="p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Upcoming Events</h3>
              <p className="text-sm text-gray-500">Next events</p>
            </div>
            <div>
              <Link to="/user/events">
                <Button size="sm" variant="outline">
                  All
                </Button>
              </Link>
            </div>
          </div>

          <div className="mt-3 space-y-3" aria-live="polite">
            {loading ? (
              <div>Loading...</div>
            ) : events.length === 0 ? (
              <div className="text-sm text-gray-500">No upcoming events</div>
            ) : (
              events.map((e) => (
                <article
                  key={e.id}
                  className="rounded-lg bg-white p-3 shadow-sm dark:bg-navy-700 dark:text-gray-200"
                  role="article"
                  aria-labelledby={`evt-${e.id}-title`}
                  tabIndex={0}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4
                        id={`evt-${e.id}-title`}
                        className="text-sm font-semibold"
                      >
                        {e.name}
                      </h4>
                      <div className="text-xs text-gray-500">
                        {e.event_date}
                      </div>
                    </div>
                    <div>
                      {e.rsvpStatus === "going" ? (
                        <Button
                          size="sm"
                          variant="primary"
                          aria-pressed="true"
                          disabled
                        >
                          RSVPed
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => handleRsvp(e.id)}
                          aria-label={`RSVP to ${e.name}`}
                        >
                          RSVP
                        </Button>
                      )}
                    </div>
                  </div>
                </article>
              ))
            )}
          </div>
        </Card>

        <Card extra="p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Quick Links</h3>
              <p className="text-sm text-gray-500">Navigate your account</p>
            </div>
          </div>

          <div className="mt-3 flex flex-col gap-2">
            {hasBusiness ? (
              <Link to="/user/business/edit">
                <Button variant="primary" size="sm">
                  Edit My Business
                </Button>
              </Link>
            ) : (
              <Link to="/user/business/create">
                <Button variant="primary" size="sm">
                  Create Business Listing
                </Button>
              </Link>
            )}
            <Link to="/user/businesses">
              <Button variant="outline" size="sm">
                View All Businesses
              </Button>
            </Link>
            <Link to="/user/documents">
              <Button variant="outline" size="sm">
                Documents
              </Button>
            </Link>
            <Link to="/user/notifications">
              <Button variant="outline" size="sm">
                Notifications
              </Button>
            </Link>
            <Link to="/user/age-grade">
              <Button variant="outline" size="sm">
                Age Grade
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default UserDefault;
